import { FC, Dispatch, SetStateAction } from 'react';

import {
  Header,
  Button,
  SpaceBetween,
  Table,
  Flashbar,
  Box,
  TextFilter,
  Pagination,
  ButtonDropdown
} from '@awsui/components-react';
import { useCollection } from '@awsui/collection-hooks';

import { FlashbarProps } from '@awsui/components-react';
import { IUserReactivationRequest } from '../../../../../interfaces/domain/userReactivationRequest';

import RejectUserReactivationRequestForm from '../rejectUserReactivationRequestForm';
import UserReactivationRequestDetails from '../userReactivationRequestDetails';
import ChildModal from '../../../../common/ChildModal';

interface ViewProps {
  userReactivationRequests: IUserReactivationRequest[];
  selectedUserReactivationRequests: IUserReactivationRequest[];
  flashBarItems: FlashbarProps.MessageDefinition[];
  setFlashBarItems: Dispatch<SetStateAction<FlashbarProps.MessageDefinition[]>>;
  disableActionButtons: boolean;
  handleTableRowSelection: (user: IUserReactivationRequest[]) => void;
  tableIsLoading: boolean;
  setRejectUserReactivationRequestModalIsVisible: Dispatch<SetStateAction<boolean>>;
  rejectUserReactivationRequestModalIsVisible: boolean;
  handleApproval: () => void;
  refreshUserReactivationRequestTable: () => void;
  setUserReactivationRequestDetailsModalIsVisible: Dispatch<SetStateAction<boolean>>;
  userReactivationRequestDetailsModalIsVisible: boolean;
}

const View: FC<ViewProps> = ({
  userReactivationRequests,
  selectedUserReactivationRequests,
  flashBarItems,
  setFlashBarItems,
  handleTableRowSelection,
  disableActionButtons,
  tableIsLoading,
  setRejectUserReactivationRequestModalIsVisible,
  rejectUserReactivationRequestModalIsVisible,
  handleApproval,
  refreshUserReactivationRequestTable,
  userReactivationRequestDetailsModalIsVisible,
  setUserReactivationRequestDetailsModalIsVisible
}) => {
  const renderEmptyTableContent = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No User Reactivation Requests</b>
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          No User Reactivation Requests to display.
        </Box>
      </Box>
    );
  };
  const renderEmptyTableContentAfterFiltering = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No matching User Reactivation Requests</b>
      </Box>
    );
  };

  const {
    items,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps
  } = useCollection(userReactivationRequests, {
    filtering: {
      empty: renderEmptyTableContent(),
      noMatch: renderEmptyTableContentAfterFiltering(),
      fields: [
        'firstName',
        'lastName',
        'corpEmail',
        'justification'
      ]
    },
    pagination: { pageSize: 10 },
    sorting: {},
    selection: {}
  });
  const { selectedItems: filteredUserReactivationRequests = [] } = collectionProps;

  const renderTableActions = () => {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="link"
            onClick={() => refreshUserReactivationRequestTable()}
          />
          <Button
            variant="primary"
            disabled={disableActionButtons}
            onClick={() => setUserReactivationRequestDetailsModalIsVisible(true)}
          >
            View
          </Button>
          <ButtonDropdown
            id="reactivate-request-actions-dropdown"
            disabled={disableActionButtons}
            items={[
              {
                text: 'Approve',
                id: 'approve-request',
                disabled: disableActionButtons
              },
              {
                text: 'Reject',
                id: 'reject-request',
                disabled: disableActionButtons
              }
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'approve-request') {
                handleApproval();
              } else if (detail.id === 'reject-request') {
                setRejectUserReactivationRequestModalIsVisible(true);
              }
            }}
          >
            Actions
          </ButtonDropdown>
        </SpaceBetween>
      </>
    );
  };
  const renderTableHeader = () => {
    return (
      <Header
        counter={
          filteredUserReactivationRequests.length
            ? `(${filteredUserReactivationRequests.length}/${userReactivationRequests.length})`
            : `(${userReactivationRequests.length})`
        }
        actions={renderTableActions()}
      >
        Pending User Reactivation Requests
      </Header>
    );
  };

  const renderRejectUserReactivationRequestModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setRejectUserReactivationRequestModalIsVisible(false);
        }}
        visible={rejectUserReactivationRequestModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Reject User Reactivation Request"
      >
        <RejectUserReactivationRequestForm
          userReactivationRequests={selectedUserReactivationRequests}
          setFlashBarItems={setFlashBarItems}
          setRejectUserReactivationRequestModalIsVisible={
            setRejectUserReactivationRequestModalIsVisible
          }
          refreshUserReactivationRequestTable={refreshUserReactivationRequestTable}
        />
      </ChildModal>
    );
  };

  const renderUserReactivationRequestDetailsModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setUserReactivationRequestDetailsModalIsVisible(false);
        }}
        visible={userReactivationRequestDetailsModalIsVisible}
        closeAriaLabel="Close modal"
        size="large"
        header="User Reactivation Request Details"
      >
        <UserReactivationRequestDetails
          selectedUserReactivationRequests={selectedUserReactivationRequests}
        />
      </ChildModal>
    );
  };

  const tableColumnDefinitions = [
    {
      id: 'firstName',
      header: 'First Name',
      cell: (item: IUserReactivationRequest) => item.firstName || '-'
    },
    {
      id: 'lastName',
      header: 'Last Name',
      cell: (item: IUserReactivationRequest) => item.lastName || '-'
    },
    {
      id: 'corpEmail',
      header: 'Email',
      cell: (item: IUserReactivationRequest) => item.corpEmail || '-'
    },
    {
      id: 'justification',
      header: 'Justification',
      cell: (item: IUserReactivationRequest) => item.justification || '-'
    }
  ];

  return (
    <>
      <SpaceBetween direction="vertical" size="xs">
        <Flashbar items={flashBarItems} />
        <Table
          id="reactivate-requests-pending-table"
          {...collectionProps}
          loading={tableIsLoading}
          trackBy="uuid"
          selectedItems={selectedUserReactivationRequests}
          selectionType="single"
          columnDefinitions={tableColumnDefinitions}
          items={items}
          loadingText="Loading Requests"
          onSelectionChange={({ detail }) =>
            handleTableRowSelection(detail.selectedItems)
          }
          header={renderTableHeader()}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} matches`}
              filteringAriaLabel="Filter Requests"
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`
              }}
            />
          }
        />
      </SpaceBetween>
      {renderRejectUserReactivationRequestModal()}
      {renderUserReactivationRequestDetailsModal()}
    </>
  );
};
export default View;
